import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { Box, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import AppLoader from '@parkdna/ui/AppLoader';
import { useTheme } from '@parkdna/ui/theme';
import { useAuth } from 'hooks/auth/useAuth';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { getAuth0Audience } from 'legacy/data/apiHelpers';
import { AnimatePresence } from 'motion/react';
import { memo, Suspense, useContext, useEffect, useState } from 'react';
import { Outlet, ScrollRestoration, useSearchParams } from 'react-router-dom';

import ForbiddenScreen from '@/components/auth/ForbiddenScreen';
import ErrorFallback from '@/components/ErrorBoundaryFallback.tsx';
import ForbiddenBoundary from '@/components/ForbiddenBoundary';
import DynamicHead from '@/components/head/DynamicHead';
import Breadcrumbs from '@/components/layout/Breadcrumbs';
import Sidebar from '@/components/layout/sidebar/Sidebar';
import Topbar from '@/components/layout/topbar/Topbar';
import MaintenanceScreen from '@/components/MaintenanceScreen';
import { TimezoneSetupDialog } from '@/components/TimezoneSetupDialog';
import ValidatorTour from '@/components/tours/validator/ValidatorTour';
import { BreadcrumbsProvider } from '@/contexts/breadcrumbs';
import SidebarMenuItemsContext from '@/contexts/sidebar-menu-items';
import { useIsOnline } from '@/hooks/useIsOnline';
import { toRumUser } from '@/lib/observability/rumPrivacy';
import { useSidebarStore } from '@/stores/sidebar';
import { useToursStore } from '@/stores/tours';

function RootLayout() {
  // NOTE: ErrorBoundary has to be here as well as in index.tsx
  // Router by default catches all errors and displays custom UI which is not a desired behaviour
  // Workaround is to define it here and since we don't use loaders or any features of rr we're good.
  // Outer error boundary in index.tsx will catch all other errors that might be inside this error boundary
  // or outer components/providers
  //  https://github.com/remix-run/react-router/discussions/10166
  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <Suspense fallback={<AppLoader />}>
        <AppRoot />
      </Suspense>
    </ErrorBoundary>
  );
}

const AppRoot = memo(() => {
  const [searchParams] = useSearchParams();
  const [authInvitationId, organizationIdOrName] = [
    searchParams.get('invitation'),
    searchParams.get('organization'),
  ];
  const isSidebarCollapsed = useSidebarStore((state) => state.isCollapsed);
  const [broken, setBroken] = useState(false);
  const sidebarMenuItems = useContext(SidebarMenuItemsContext);
  const isOnline = useIsOnline();
  const errorMessageOpenID = searchParams.get('error');
  const {
    user,
    signOut,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    hasAccessToPortal,
    roles,
    orgName,
    orgMetadata,
  } = useAuth();
  const ldClient = useLDClient();
  const flags = useFlags();
  const [theme] = useTheme();
  const [isValidatorTourRun, setValidatorTourRun] = useToursStore((state) => [
    state.isValidatorTourRun,
    state.setIsValidatorTourRun,
  ]);

  useEffect(() => {
    if (ldClient && user) {
      void ldClient.identify({
        key: user.sub,
        email: user.email,
        name: user.name,
        kind: 'user',
        privateAttributeNames: ['email', 'name'],
      });
    }
  }, [ldClient, user]);

  useEffect(() => {
    if (!user) return;

    datadogRum.setUser(toRumUser(user.sub));

    datadogRum.setUserProperty('roles', roles);
    datadogRum.setUserProperty('org', orgName);
  }, [user, roles, orgName]);

  useEffect(() => {
    // TODO: log error to datadog or similar
    if (errorMessageOpenID) {
      void signOut();
      return;
    }
  }, [errorMessageOpenID, isLoading, signOut]);

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    if (authInvitationId && organizationIdOrName) {
      void loginWithRedirect({
        authorizationParams: {
          organization: organizationIdOrName,
          invitation: authInvitationId,
          audience: getAuth0Audience(),
        },
      });
      return;
    }

    void (async (): Promise<void> => {
      await loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
    })();
  }, [
    loginWithRedirect,
    isLoading,
    isAuthenticated,
    authInvitationId,
    organizationIdOrName,
  ]);

  if (flags.maintenanceMode?.enabled) {
    return <MaintenanceScreen message={flags.maintenanceMode.message} />;
  }

  return (
    <>
      <AnimatePresence>
        {!isAuthenticated && (
          <AppLoader
            key="app-loader"
            logoSrc={orgMetadata?.['favicon_svg_url'] ?? '/favicon.svg'}
          />
        )}
      </AnimatePresence>
      {isAuthenticated &&
        (!hasAccessToPortal ? (
          <ForbiddenScreen autoLogout />
        ) : (
          <>
            <TimezoneSetupDialog />
            {!isOnline && (
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: (theme) => theme.zIndex.appBar + 1,
                }}
              >
                <Box
                  sx={{
                    bgcolor: (theme) => theme.palette.warning.main,
                    minHeight: '1.25rem',
                    p: {
                      xs: 1,
                      md: 0,
                    },
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography variant="body2">
                    You&apos;re offline. Please check your internet connection.
                  </Typography>
                </Box>
              </Box>
            )}
            <Stack direction="row">
              <ValidatorTour
                run={isValidatorTourRun}
                onFinish={() => setValidatorTourRun(false)}
              />
              <DynamicHead />
              <Sidebar
                broken={broken}
                setBroken={setBroken}
                menuItems={sidebarMenuItems}
              />
              <Stack
                sx={{
                  flexGrow: 1,
                  marginLeft: 'auto',
                  maxWidth: broken
                    ? '100%'
                    : `calc(100% - ${isSidebarCollapsed ? `${theme.layout.sidebar.collapsed}px` : `${theme.layout.sidebar.regular}px`})`,
                  minHeight: '100vh',
                  transition: 'width,left,right,300ms',
                }}
                direction="column"
                component="main"
              >
                <Topbar />
                <BreadcrumbsProvider>
                  <Breadcrumbs />
                  <ForbiddenBoundary>
                    <Outlet />
                  </ForbiddenBoundary>
                </BreadcrumbsProvider>
                <ScrollRestoration />
              </Stack>
            </Stack>
          </>
        ))}
    </>
  );
});
AppRoot.displayName = 'AppRoot';

export default RootLayout;
